import { Separator } from "@/components/ui/baseUi/separator"
import { Skeleton } from "@/components/ui/baseUi/skeleton"
import { word } from "@modules/core/utils/i18n"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"

type PayoutDetailsInfoSkeletonProps = {
  className?: string
} & React.ComponentProps<"div">

export function PayoutDetailsInfoSkeleton({ className, ...props }: PayoutDetailsInfoSkeletonProps) {
  const summaryHeaders = [
    { label: word("finances.payoutDetails.summaryHeaders.quantity"), col: "col-span-1" },
    { label: word("finances.payoutDetails.summaryHeaders.gross"), col: "col-span-2" },
    { label: word("finances.payoutDetails.summaryHeaders.fees"), col: "col-span-2" },
    { label: word("finances.payoutDetails.summaryHeaders.total"), col: "col-span-2" },
  ]

  const salesHeaders = [
    { label: word("finances.payoutDetails.salesHeaders.type"), col: "col-span-3" },
    { label: word("finances.payoutDetails.salesHeaders.gross"), col: "col-span-2" },
    { label: word("finances.payoutDetails.salesHeaders.net"), col: "col-span-2" },
    { label: word("finances.payoutDetails.salesHeaders.name"), col: "col-span-3" },
    { label: word("finances.payoutDetails.salesHeaders.date"), col: "col-span-2" },
  ]

  const payoutTransactionSummary = {
    payment: { label: word("finances.payoutDetails.transactionSummary.payment") },
    refund: { label: word("finances.payoutDetails.transactionSummary.refund") },
    adjustment: {
      label: word("finances.payoutDetails.transactionSummary.adjustement"),
    },
  }

  return (
    <div className={cn("relative", className)} {...props}>
      {/* HEADER */}
      <div className="relative flex w-full justify-between rounded-b-4xl bg-surface-foreground px-7 pt-14 pb-6">
        {/* Column paid */}
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-1">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="mt-1 h-8 w-16" />
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <Text fontStyle="LS" color="text-textDefault-caption">
              {word("finances.payoutDetails.accountInformation")}
            </Text>
            <div className="flex">
              <Skeleton className="mt-1 h-6 w-60" />
            </div>
          </div>
        </div>
        {/* Column timeline */}
        <div className="flex flex-col">
          <Text fontStyle="BS" color="text-textDefault-caption" className="mb-3">
            {word("finances.payoutDetails.timeline")}
          </Text>

          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="flex size-4 rounded-md bg-primary-500"></div>
              <div className="flex flex-col gap-1">
                <Text fontStyle="LM" color="text-textDefault-caption">
                  {word("finances.payoutDetails.transferMade")}
                </Text>
                <Skeleton className="mt-1 h-3 w-42" />
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex size-4 rounded-md bg-neutral-600"></div>
              <div className="flex flex-col gap-1">
                <Text fontStyle="LS" color="text-textDefault-caption">
                  {word("finances.payoutDetails.transferInTransit")}
                </Text>
                <Skeleton className="mt-1 h-3 w-42" />
              </div>
            </div>

            <div className="flex gap-2">
              <div className="flex size-4 rounded-md bg-neutral-600"></div>
              <div className="flex flex-col gap-1">
                <Text fontStyle="LS" color="text-textDefault-caption">
                  {word("finances.payoutDetails.automatedTransfer")}
                </Text>
                <Skeleton className="mt-1 h-3 w-42" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*CONTENT */}
      <div className="flex w-full flex-col px-6">
        {/* SUMMARY */}
        <div className="mt-6 flex w-full flex-col">
          <Text fontStyle="LM" color="text-textDefault-caption">
            {word("finances.payoutDetails.headerTab.summary").toUpperCase()}
          </Text>
          <div className="grid grid-cols-10">
            <div className="col-span-3"></div>
            {summaryHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SUMMARY`}>
                <Text fontStyle="LM" color="text-textDefault-caption">
                  {header.label}
                </Text>
              </div>
            ))}
          </div>
          <Separator />
          {Object.values(payoutTransactionSummary).map((summary) => (
            <div className="flex w-full flex-col pt-2" key={summary.label}>
              <div className="grid grid-cols-10 items-end">
                <div className="col-span-3">
                  <Text fontStyle="LM" className="text-textDefault-heading">
                    {summary.label}
                  </Text>
                </div>
                <div className="col-span-1">
                  <Skeleton className="h-6 w-8" />
                </div>
                <div className="col-span-2">
                  <Skeleton className="h-6 w-16" />
                </div>
                <div className="col-span-2">
                  <Skeleton className="h-6 w-12" />
                </div>
                <div className="col-span-2">
                  <Skeleton className="h-6 w-16" />
                </div>
              </div>
              <Separator />
            </div>
          ))}
        </div>
        {/* TRANSFER */}
        <div className="mt-3 flex w-full items-baseline justify-end gap-4">
          <Text fontStyle="LM" color="text-textDefault-heading">
            {word("finances.payoutDetails.transfer")}
          </Text>
          <Skeleton className="h-5 w-20" />
        </div>
        {/* SALES */}
        <div className="mt-6 flex w-full flex-col">
          <div className="mb-3 flex w-full items-center justify-between">
            <Text fontStyle="LM" color="text-textDefault-caption">
              {word("finances.payoutDetails.headerTab.sales").toUpperCase()}
            </Text>
            <Skeleton className="h-8 w-32" />
          </div>
          <div className="grid w-full grid-cols-10 px-2">
            {salesHeaders.map((header) => (
              <div className={header.col} key={`${header.label}_SALES`}>
                <Text fontStyle="LM" className="break-all text-textDefault-caption">
                  {header.label}
                </Text>
              </div>
            ))}
          </div>
          <Separator />
          <Skeleton className="mt-1 h-80 w-full" />
        </div>
        {/* FOOTER */}
        <div className="mt-6 mb-4 flex w-full flex-col gap-2">
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" color="text-textDefault-caption">
              {word("finances.payoutDetails.internalNote")}
            </Text>
            <Skeleton className="mt-1 h-4 w-28" />
          </div>
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" color="text-textDefault-caption">
              {word("finances.payoutDetails.transferMethod")}
            </Text>
            <Skeleton className="mt-1 h-4 w-20" />
          </div>
          <div className="flex flex-col gap-1">
            <Text fontStyle="BS" color="text-textDefault-caption">
              {word("finances.payoutDetails.transferTrackingId")}
            </Text>
            <Skeleton className="mt-1 h-4 w-60" />
          </div>
        </div>
      </div>
    </div>
  )
}
